import React, { useContext } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { translateBlock } from 'i18n';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import TreeValueContext, {
    TreeValue,
} from 'routes/Explore/components/Sentence/contexts/TreeValueContext';
import { TreeData } from 'routes/Explore/components/Sentence/helpers/treeData';
import Categories from './Categories';
import { TreeItem, TreeView } from '@mui/x-tree-view';

const StyDomainTreeItem = styled(TreeItem, {
    name: 'StyDomainTreeItem',
})(({ theme }) => ({
    marginTop: '0.5rem',
    '& > .MuiTreeItem-content': {
        alignItems: 'start',
    },
    [theme.breakpoints.up('md')]: {
        // increase the indentation between the domain title and its subtree
        '& > .MuiTreeItem-group': {
            marginLeft: '2rem',
        },
    },
}));

const StyTreeView = styled(TreeView, {
    name: 'StyTreeView',
})({
    width: '100%',
    overflow: 'auto',
    height: '100%',
    padding: '0 0 1.5rem 1.5rem',
});

type Domain = {
    value: string;
    label: string;
};

type Props = {
    treeData: TreeData;
    onClick: (field: TreeValue) => void;
    testid: string;
    expanded: string[];
    query: string;
    value?: TreeValue;
    onNodeToggle: FlowAnyFunction;
};

const DOMAINS_DISPLAYED_AT_THE_TREE_END = ['global', 'CSV', 'VIEW'];

const Domains = ({
    treeData,
    onClick,
    testid,
    expanded,
    query,
    value,
    onNodeToggle,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const { currentValues } = useContext(TreeValueContext);
    const sortedDomains: Domain[] = _(treeData)
        .keys()
        .map((domain) => ({
            value: domain,
            label: translateBlock(domain.toLowerCase()),
        }))
        .sort((a, b) => {
            if (
                DOMAINS_DISPLAYED_AT_THE_TREE_END.includes(a.value) &&
                DOMAINS_DISPLAYED_AT_THE_TREE_END.includes(b.value)
            ) {
                return (
                    DOMAINS_DISPLAYED_AT_THE_TREE_END.indexOf(a.value) -
                    DOMAINS_DISPLAYED_AT_THE_TREE_END.indexOf(b.value)
                );
            }
            if (DOMAINS_DISPLAYED_AT_THE_TREE_END.includes(a.value)) return 1;
            if (DOMAINS_DISPLAYED_AT_THE_TREE_END.includes(b.value)) return -1;
            return a.label > b.label ? 1 : -1;
        })
        .value();

    const renderDomain = (domain: Domain) => (
        <StyDomainTreeItem
            key={domain.value}
            nodeId={domain.value}
            label={
                <Box
                    sx={{
                        display: { xs: 'block', md: 'flex' },
                        alignItems: 'baseline',
                    }}
                >
                    <Typography
                        variant="body1SemiBold"
                        sx={{
                            display: 'flex',
                            flexShrink: 0,
                        }}
                    >
                        {domain.label}
                    </Typography>
                </Box>
            }
        >
            <Categories
                treeData={treeData}
                onClick={onClick}
                domain={domain.value}
                testid={testid}
            />
        </StyDomainTreeItem>
    );

    return sortedDomains.length === 0 ? (
        <Typography sx={{ p: '0 1.5rem 1.5rem 1.5rem', flexShrink: 0 }}>
            {query ? t('explore.noFieldMatch') : t('explore.noFields')}
        </Typography>
    ) : (
        <StyTreeView
            defaultCollapseIcon={<ExpandMoreIcon />}
            defaultExpandIcon={<ChevronRightIcon />}
            expanded={expanded}
            onNodeToggle={onNodeToggle}
            selected={`field-${value || currentValues[0]}`}
        >
            {sortedDomains.map(renderDomain)}
        </StyTreeView>
    );
};

export default Domains;
